/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
} from "@arcadeai/design-system/rhf";
import { InfoIcon, PlusCircle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

const transformParams = (
  params: Array<{ name: string; value: string }>
): Record<string, string> =>
  params.reduce(
    (acc, param) => {
      if (param.name.trim()) {
        acc[param.name] = param.value;
      }
      return acc;
    },
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    {} as Record<string, string>
  );

const transformRecordToParams = (
  record: Record<string, string>
): Array<{ id: string; name: string; value: string }> =>
  Object.entries(record).map(([name, value]) => ({
    id: crypto.randomUUID(),
    name,
    value,
  }));

type RequestParametersInputProps = {
  value?: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  // biome-ignore lint/suspicious/noExplicitAny: Form type requires any for flexibility
  form?: UseFormReturn<any>;
  name?: string;
};

export const RequestParametersInput = ({
  value = {},
  onChange,
}: RequestParametersInputProps) => {
  const [params, setParams] = useState<
    Array<{ id: string; name: string; value: string }>
  >([]);

  const isInitialized = useRef(false);
  const isInternalUpdate = useRef(false);

  // Initialize params from value prop only once or when externally changed
  useEffect(() => {
    if (!isInitialized.current) {
      // Initial load
      const initialParams = transformRecordToParams(value);
      setParams(initialParams);
      isInitialized.current = true;
    } else if (!isInternalUpdate.current) {
      // External change (not from our internal updates)
      const currentRecord = transformParams(params);
      const valueKeys = Object.keys(value).sort();
      const currentKeys = Object.keys(currentRecord).sort();

      // Only update if the structure actually changed
      if (
        valueKeys.length !== currentKeys.length ||
        valueKeys.some((key, index) => key !== currentKeys[index]) ||
        valueKeys.some((key) => value[key] !== currentRecord[key])
      ) {
        const newParams = transformRecordToParams(value);
        setParams(newParams);
      }
    }

    // Reset the internal update flag
    isInternalUpdate.current = false;
  }, [value, params]);

  // Helper function to update onChange with Record format
  const updateRecord = useCallback(
    (updatedParams: Array<{ id: string; name: string; value: string }>) => {
      isInternalUpdate.current = true;
      const record = transformParams(updatedParams);
      onChange(record);
    },
    [onChange]
  );

  const handleAddItem = useCallback(() => {
    const newParams = [
      ...params,
      { id: crypto.randomUUID(), name: "", value: "" },
    ];
    setParams(newParams);
    // Don't update onChange for empty entries
  }, [params]);

  const handleRemoveItem = useCallback(
    (id: string) => {
      const newParams = params.filter((param) => param.id !== id);
      setParams(newParams);
      updateRecord(newParams);
    },
    [params, updateRecord]
  );

  const handleParamChange = useCallback(
    (id: string, field: "name" | "value", newValue: string) => {
      const updatedParams = params.map((param) =>
        param.id === id ? { ...param, [field]: newValue } : param
      );

      setParams(updatedParams);
      updateRecord(updatedParams);
    },
    [params, updateRecord]
  );

  return (
    <TooltipProvider>
      <div className="space-y-3 rounded-lg border border-zinc-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FormLabel className="font-medium text-sm">
              Request Parameters
            </FormLabel>
            <Tooltip>
              <TooltipTrigger type="button">
                <InfoIcon className="size-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="w-auto max-w-xs" side="top">
                <p className="text-sm">
                  Define parameters for the introspection request. You can use
                  placeholders like{" "}
                  <code>
                    {"{{"}'access_token'{"}}"}
                  </code>{" "}
                  in the value field.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Button
            className="flex items-center gap-x-1.5"
            onClick={handleAddItem}
            size="sm"
            type="button"
            variant="outline"
          >
            <PlusCircle className="h-4 w-4 text-green-500" />
            Add Parameter
          </Button>
        </div>
        {params.length > 0 && (
          <FormDescription>
            Define key-value pairs for the introspection request.
          </FormDescription>
        )}
        <div className="space-y-3">
          {params.map((item) => (
            <div className="flex items-start gap-2" key={item.id}>
              <FormItem className="flex-1">
                <FormControl>
                  <Input
                    onChange={(e) =>
                      handleParamChange(item.id, "name", e.target.value)
                    }
                    placeholder="e.g., token"
                    value={item.name}
                  />
                </FormControl>
              </FormItem>
              <FormItem className="flex-1">
                <FormControl>
                  <Input
                    onChange={(e) =>
                      handleParamChange(item.id, "value", e.target.value)
                    }
                    placeholder="e.g., {{access_token}}"
                    value={item.value}
                  />
                </FormControl>
              </FormItem>
              <Button
                className="mt-1 text-destructive hover:text-destructive/80"
                onClick={() => handleRemoveItem(item.id)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Remove Parameter</span>
              </Button>
            </div>
          ))}
        </div>
        {params.length === 0 && (
          <p className="text-muted-foreground text-sm">
            No request parameters added. Click "Add Parameter" to add one.
          </p>
        )}
      </div>
    </TooltipProvider>
  );
};
